<script setup lang="ts">
import { computed, watch } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useMapsStore } from '@/stores/maps'
import { useRoute } from 'vue-router'

import { UserRoundPlus } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import DialogAddMember from './DialogAddMember.vue'
import MembersList from './MembersList.vue'
import InvitesList from './InvitesList.vue'

const orgStore = useOrgStore()

const mapsStore = useMapsStore()
const route = useRoute()

const mapId = computed(() => route.params.map_id as string)
const orgId = computed(() => orgStore.selectedOrg as string)
const map = computed(() => mapsStore.getMapById(orgId.value, mapId.value))
const members = computed(() => mapsStore.getMapMembers(mapId.value))
const invites = computed(() => mapsStore.getMapInvites(mapId.value))
const pendingInvites = computed(() => invites.value.filter((i: any) => !i.is_accepted))

watch(
  [orgId, mapId],
  ([org, map]) => {
    if (!org || !map) return
    mapsStore.fetchMapMembers({ orgId: org, mapId: map })
    mapsStore.fetchMapInvites({ orgId: org, mapId: map })
  },
  { immediate: true }
)
</script>

<template>
  <div class="px-3 py-2" data-testid="map-members-page">
    <div class="mb-4">
      <h2 class="text-lg font-semibold">{{ map?.name || 'Map' }} — Members</h2>
      <p class="text-sm text-muted-foreground">People with access to this map.</p>
    </div>

    <div class="mb-1 flex items-center justify-between">
      <h3 class="text-base font-bold">Members ({{ members.length }})</h3>
      <DialogAddMember>
        <Button variant="ghost" size="sm" class="px-2">
          <UserRoundPlus class="w-4 h-4 mr-1" />
          Add
        </Button>
      </DialogAddMember>
    </div>

    <MembersList :members="members" :map-id="mapId" />

    <div class="mt-6 mb-2">
      <h3 class="text-base font-bold">Pending invites ({{ pendingInvites.length }})</h3>
    </div>
    <InvitesList v-if="pendingInvites.length" :invites="pendingInvites" :map-id="mapId" />
    <p v-else class="text-sm text-muted-foreground">No pending invites.</p>
  </div>
</template>
